// Some senders leak their template context into the text/plain part as Go's
// `%v` rendering of a map — "map[actionUrl:… policies:[A B C] …]" — burying the
// content the reader needs in machine keys, nested transport maps and inline
// base64. Only the collections survive the trip: Go prints a slice without
// delimiters, and every scalar in such a dump is a template variable already
// rendered elsewhere in the message.
const MAP_PREFIX = "map[";

const ENTRY_START_REGEX = /^[A-Za-z_][A-Za-z0-9_]*:/;
const ENTRY_KEY_AT_REGEX = /[A-Za-z_][A-Za-z0-9_]*:/y;
const TRAILING_SPACES_REGEX = /[ \t]+$/;
const LEADING_SPACES_REGEX = /^[ \t]+/;
const UNBROKEN_BLOB_REGEX = /\S{200,}/;

const MIN_DUMP_ENTRIES = 2;
const MIN_LIST_ITEMS = 2;
const MIN_LIST_ITEM_WORDS = 2;

const findBalancedEnd = (text: string, openIndex: number): number => {
  let depth = 0;

  for (let index = openIndex; index < text.length; index += 1) {
    if (text[index] === "[") {
      depth += 1;
    } else if (text[index] === "]") {
      depth -= 1;

      if (depth === 0) {
        return index;
      }
    }
  }

  return -1;
};

const splitEntries = (content: string): string[] => {
  const entries: string[] = [];
  let depth = 0;
  let start = 0;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];

    if (char === "[") {
      depth += 1;
    } else if (char === "]") {
      depth -= 1;
    } else if (char === " " && depth === 0) {
      ENTRY_KEY_AT_REGEX.lastIndex = index + 1;

      if (ENTRY_KEY_AT_REGEX.test(content)) {
        entries.push(content.slice(start, index));
        start = index + 1;
      }
    }
  }

  entries.push(content.slice(start));

  return entries.filter(Boolean);
};

const isGoMapDump = (entries: string[]): boolean =>
  entries.length >= MIN_DUMP_ENTRIES &&
  entries.every((entry) => ENTRY_START_REGEX.test(entry));

// Go prints a []string as "[a b c]", so item boundaries are unrecoverable in
// general. They are recoverable when every item ends in the same word, which is
// the usual shape of these lists ("… Policy", "… Report").
const toBulletList = (items: string): string | null => {
  const words = items.split(/\s+/);
  const lastWord = words[words.length - 1];
  const grouped: string[] = [];
  let current: string[] = [];

  words.forEach((word) => {
    current.push(word);

    if (word === lastWord) {
      grouped.push(current.join(" "));
      current = [];
    }
  });

  if (
    current.length ||
    grouped.length < MIN_LIST_ITEMS ||
    grouped.some((item) => item.split(" ").length < MIN_LIST_ITEM_WORDS)
  ) {
    return null;
  }

  return grouped.map((item) => `- ${item}`).join("\n");
};

const toReadableBlock = (entries: string[]): string =>
  entries
    .map((entry) => {
      const value = entry.slice(entry.indexOf(":") + 1);

      if (!value.startsWith("[") || !value.endsWith("]")) {
        return null;
      }

      const items = value.slice(1, -1).trim();

      if (!items || UNBROKEN_BLOB_REGEX.test(items)) {
        return null;
      }

      return toBulletList(items) ?? items;
    })
    .filter(Boolean)
    .join("\n\n");

export const unwrapMachineDump = (text: string): string => {
  if (!text.includes(MAP_PREFIX)) {
    return text;
  }

  let unwrapped = "";
  let rest = text;

  for (;;) {
    const mapIndex = rest.indexOf(MAP_PREFIX);

    if (mapIndex === -1) {
      break;
    }

    const endIndex = findBalancedEnd(rest, mapIndex + MAP_PREFIX.length - 1);

    if (endIndex === -1) {
      break;
    }

    const entries = splitEntries(
      rest.slice(mapIndex + MAP_PREFIX.length, endIndex)
    );

    if (isGoMapDump(entries)) {
      const before = rest.slice(0, mapIndex).replace(TRAILING_SPACES_REGEX, "");
      const block = toReadableBlock(entries);

      unwrapped += block ? `${before}\n\n${block}\n\n` : `${before} `;
      rest = rest.slice(endIndex + 1).replace(LEADING_SPACES_REGEX, "");
    } else {
      unwrapped += rest.slice(0, endIndex + 1);
      rest = rest.slice(endIndex + 1);
    }
  }

  return unwrapped + rest;
};
